<script setup lang="ts">
    /**
     * The app-wide overlays behind every open modal: a clickable one that closes the
     * latest modal, and a blurred tint underneath. Moved as-is out of `layout/Modals.vue`
     * so that following a sheet's swipe (`useSheetSwipeBackdrop`) only re-renders these
     * two elements — `Modals.vue` mounts every modal of the app, and binding a value that
     * changes on each finger move there would re-run its whole render.
     *
     * Idle, `backdropStyle` is `undefined` and the markup is exactly what it was before
     * the move, which is what keeps desktop modals, sharing these overlays, untouched.
     */
    import { storeToRefs } from 'pinia';
    import { sheetBackdropStyle } from '~/composables/useSheetSwipeBackdrop';

    const modalsStore = useModalsStore();
    const { closeLatestModal, getClickableOverlayZIndex } = modalsStore;
    const { displayOverlay } = storeToRefs(modalsStore);

    const backdropStyle = computed(() => sheetBackdropStyle(modalsStore.sheetBackdrop, modalsStore.sheetBackdrop.syncGlobal));
</script>

<template>
    <transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-200 ease-out delay-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
    >
        <div
            v-show="displayOverlay"
            :style="[{ zIndex: getClickableOverlayZIndex() }, backdropStyle]"
            class="fixed inset-0 bg-branding-dark-grey/30 backdrop-blur-[1px]"
            @click="closeLatestModal"
        ></div>
    </transition>
    <transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-200 ease-out delay-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
    >
        <div
            v-show="displayOverlay"
            :style="backdropStyle"
            :class="[$theme('modal.overlay'), 'fixed inset-0 backdrop-blur-[2px] z-[890] bg-[#0a183a45]']"
        ></div>
    </transition>
</template>
